<template>
    <div class="container-fluid">
        <h1>{{$t(viewTitle)}}</h1>
        <div class="card card-body shadow" ref="container">
            <div class="row" v-if="compMedia !== null">
                <div class="col-sm-12 col-md">
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb noselect">
                            <li class="breadcrumb-item" v-for="(item, index) in breadcrumb" :key="item.path">
                                <a href="javascript:void(0)" :class="index === (breadcrumb.length - 1) ? 'active' : ''" @click="getContent(item.path)">
                                    <span :class="item.classes">{{item.name}}</span>
                                </a>
                            </li>
                        </ol>
                    </nav>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-12 col-md-4" v-if="selectedFiles.length > 0">
                    <div class="border-left">
                        <div class="m-2">
                            {{$t('components.media.selected-files')}}<span class="float-right">({{selectedFiles.length}}) {{totalSize | niceBytesSize}}</span>
                            <hr>
                            <ul class="selected-files">
                                <li v-for="(file, index) in selectedFiles" :key="file.path">
                                    <button class="btn btn-sm btn-outline-wellis-black mr-2" @click="removeSelected(index)"><i class="fa fa-trash"></i></button>
                                    {{file.name | cut(35)}}.{{file.extension}}
                                </li>
                            </ul>
                            <button class="btn btn-outline-wellis-gold w-100" @click="downloadSelectedFiles">{{$t('components.media.download')}}</button>
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 col-md">
                    <div class="row" v-if="compMedia !== null">
                        <div :class="['col-sm-12 mb-3', resolveMediaItemSize]" v-if="compMedia.currentPath !== ''">
                            <MediaItem :prev="true" :parent-path="compMedia.parentPath" :current-path="compMedia.currentPath"/>
                        </div>
                        <div :class="['col-sm-12 mb-3', resolveMediaItemSize]" v-for="(item, index) in mediaItems" :key="compMedia.currentPath + '_' + index">
                            <MediaItem :item="item"
                                       :parent-path="compMedia.parentPath"
                                       :current-path="compMedia.currentPath"
                                       :selected-state="resolveSelectedState(item.path)"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <MediaImagePreview/>
        <div v-if="$store.getters['authStore/isAdmin']">
            <MediaItemRestriction :visible="restrictionTarget !== null" :media-item="restrictionTarget"/>
        </div>
    </div>
</template>

<script>
import MediaApiService from "@/api/media.api";
import MediaItem from "@/components/application/MediaItem";
import {empty, handleDownloadedFile} from "@/helpers";
import {EVENT_MEDIA_RESTRICT, EVENT_MEDIA_RESTRICT_CHANGED, EVENT_MEDIA_RESTRICT_CLOSE, EVENT_MEDIA_SELECT_FILE} from "@/core/constants";
import MediaImagePreview from "@/components/application/MediaImagePreview";
import MediaItemRestriction from "@/components/administration/media/MediaItemRestriction";
import ExplorerHistory from "@/modules/explorer";

export default {
    name: "Media",
    components: {MediaImagePreview, MediaItem, MediaItemRestriction},
    mounted() {
        ExplorerHistory.resolve(this).then(result => {
            if (!result) {
                this.getContent().then(() => {
                    setTimeout(() => {
                        this.hideLoader();
                        ExplorerHistory.saveCurrentState(this, this.$route, window.pageYOffset, this.viewTitle)
                    }, 100);
                });
            }
        });

        this.$eventBus.$on(EVENT_MEDIA_SELECT_FILE, this.handleFileSelection);
        this.$eventBus.$on(EVENT_MEDIA_RESTRICT, this.openRestrictionModal);
        this.$eventBus.$on(EVENT_MEDIA_RESTRICT_CLOSE, this.closeRestrictionModal);
        this.$eventBus.$on(EVENT_MEDIA_RESTRICT_CHANGED, this.updateMediaItemRestriction);
    },

    destroyed() {
        this.$eventBus.$off(EVENT_MEDIA_SELECT_FILE);
        this.$eventBus.$off(EVENT_MEDIA_RESTRICT);
        this.$eventBus.$off(EVENT_MEDIA_RESTRICT_CLOSE);
        this.$eventBus.$off(EVENT_MEDIA_RESTRICT_CHANGED);
    },

    beforeRouteLeave(to, from, next) {
        let loader = this.$loading.show();
        ExplorerHistory.saveCurrentState(this, from, window.pageYOffset, this.viewTitle).then(() => {
            loader.hide();
            next();
        });
    },

    data() {
        return {
            viewTitle: 'components.media.title',
            media: null,
            selectedFiles: [],
            breadcrumb: [],
            restrictionTarget: null
        }
    },

    methods: {
        getContent(path = '', parentPath = null) {
            return new Promise((resolve, reject) => {
                this.showLoader()
                    .then(() => {
                        MediaApiService.getContent(path, parentPath)
                            .then(response => {
                                this.media = response.data;
                                resolve(response.data);
                            })
                            .catch(error => {
                                reject(error);
                            })
                            .finally(() => this.hideLoader());
                    });
            });
        },

        showLoader() {
            return new Promise(resolve => {
                if (!empty(this.loader)) {
                    this.loader.hide();
                    this.loader = null;
                }
                this.loader = this.$loading.show();
                resolve(this.loader);
            });
        },

        hideLoader() {
            return new Promise(resolve => {
                if (!empty(this.loader)) {
                    this.loader.hide();
                    this.loader = null;
                }
                resolve();
            });
        },

        handleFileSelection(mediaItem) {
            let index = this.selectedFiles.findIndex(item => item.path === mediaItem.path);
            if (index > -1) {
                this.selectedFiles.splice(index, 1);
            } else {
                this.selectedFiles.push(mediaItem);
            }
            this.$forceUpdate();
        },

        downloadSelectedFiles() {
            this.showLoader();
            const files = this.selectedFiles.map(item => item.path);
            MediaApiService.multipleDownload(files)
                .then(blobFile => {
                    let filename = blobFile.headers['content-disposition'].split('filename')[1];
                    filename = filename.substr(1, filename.length);
                    this.$set(this, 'selectedFiles', []);
                    return handleDownloadedFile(blobFile.data, `media_${filename}`, blobFile.type);
                })
                .catch(() => {
                    this.$swal.fire({
                        icon: "error",
                        allowOutsideClick: false,
                        html: this.$t('api-error')
                    });
                })
                .finally(() => this.hideLoader());
        },

        openRestrictionModal(mediaItem) {
            this.restrictionTarget = mediaItem;
        },

        closeRestrictionModal() {
            this.restrictionTarget = null;
        },

        updateMediaItemRestriction(data) {
            const index = this.media.items.findIndex(item => item.path === data.path);
            if (index > -1) {
                this.media.items[index].restricted = data.restricted;
            }
        },

        removeSelected(index) {
            this.selectedFiles.splice(index, 1);
        }
    },

    computed: {
        compMedia() {
            return this.media;
        },

        mediaItems() {
            if (this.media) {
                return this.media.items;
            }
            return [];
        },

        resolveSelectedState() {
            return path => {
                return this.selectedFiles.findIndex(item => item.path === path) > -1;
            }
        },

        resolveMediaItemSize() {
            return this.selectedFiles.length > 0 ? 'col-md-4' : 'col-md-2';
        },

        totalSize() {
            let size = 0;
            this.selectedFiles.forEach(item => size += item.size);
            return size;
        }
    },

    watch: {
        'media': {
            deep: true,
            immediate: true,
            handler(val) {
                if (!empty(val)) {
                    const items = this.media.currentPath.split('/');
                    const breadcrumb = [];
                    breadcrumb.push({
                        name: '',
                        classes: 'fa fa-home',
                        path: '/'
                    });
                    if (items.length > 0 && items[0] === "") {
                        items.splice(0, 1);
                    }
                    let maxPath = '';
                    if (items.length > 0) {
                        items.forEach((item) => {
                            if (maxPath.length > 0) {
                                maxPath += '/';
                            }
                            maxPath += item;
                            breadcrumb.push({
                                name: item,
                                classes: '',
                                path: maxPath
                            });
                        });
                    }
                    this.breadcrumb = breadcrumb;
                }
            }
        }
    }
}
</script>

<style scoped>
li {
    font-weight: normal;
    margin-top: 10px;
    margin-bottom: 10px;
}

.active {
    color: #b89453;
    pointer-events: none;
}

.breadcrumb-item + .breadcrumb-item::before {
    content: '/';
}

.selected-files {
    margin: 0;
    padding: 0;
    margin-left: 5px;
    list-style: none;
}
</style>
